import { ConditionSetType, ESymbolCategory } from "@kuika/kuika-cl-model";
import type { BackEndValueType, IConditionModel, ISymbol, IWFParameter } from "@kuika/kuika-cl-model";

/**
 * KOA-18448 — the single producer of a workflow condition row written by the
 * BPMN DSL tools (gateway branch conditions and the multi-instance
 * quorum/cancel condition).
 *
 * Both call sites used to inline their own object literal into an `any`-typed
 * workflow node, and both stamped `category: 8` on the left operand. Slot 8 is
 * a RETIRED symbol category — commented out in `ESymbolCategory` on both the
 * TypeScript and the C# side — so nothing on the read side could interpret it:
 * the workflow generator only recognises `processParameter` for a condition
 * operand and hands anything else to the generic symbol translator, which has
 * no case for 8 and throws `ToClientCode => Not implemented, Symbol Category: 8`,
 * failing the WHOLE code generation run (generators run in parallel; one
 * exception aborts all of them).
 *
 * The operand shape here mirrors what the symbol picker persists for a manually
 * edited condition, field for field, because the generator reads those fields:
 *  - `description` → the parameter name the branch expression compiles to
 *    (`data.<Name>`);
 *  - `inputName` → the parameter id, as the picker stores it. It must be
 *    non-empty: the generic translator calls `StartsWith` on it unguarded;
 *  - `targetType` → the type the comparison is generated with. Leaving it unset
 *    (as both call sites did) left the branch expression with no type at all,
 *    so the right-hand operand was emitted without its conversion.
 */

/** One row of a DSL-authored condition: `<propertyName> <operator> <value>`. */
export interface IWorkflowConditionRowInput {
  readonly propertyName: string;
  readonly operator?: string;
  readonly value?: unknown;
}

/** Shorthand operators accepted from the DSL, mapped to the stored operator names. */
const OPERATOR_ALIASES: Record<string, string> = {
  "==": "isEqualTo",
  equals: "isEqualTo",
  "!=": "isNotEqualTo",
  ">": "isGreaterThan",
  ">=": "isGreaterOrEqual",
  "<": "isLessThan",
  "<=": "isLessOrEqual"
};

function newConditionId(): string {
  return globalThis.crypto ? globalThis.crypto.randomUUID() : Math.random().toString();
}

export function normalizeWorkflowConditionOperator(operator: string | undefined): string | undefined {
  if (!operator) return operator;
  return OPERATOR_ALIASES[operator] ?? operator;
}

/**
 * The left operand: a value read from the running process' parameter bag.
 * `parameter` is the workflow parameter the row names, when it could be
 * resolved; the fallbacks keep a half-resolved row writable (the caller reports
 * the unknown name) instead of persisting a symbol the generator cannot read.
 */
export function buildWorkflowParameterOperand(propertyName: string, parameter: IWFParameter | undefined): ISymbol {
  const name: string = parameter?.name ?? propertyName;
  const dataType: BackEndValueType = parameter?.dataType ?? "Unknown";
  return {
    category: ESymbolCategory.processParameter,
    label: name,
    description: name,
    fieldName: name,
    inputName: parameter?.id ?? propertyName,
    currentType: dataType,
    targetType: dataType,
    fieldType: dataType
  };
}

/**
 * The right operand: the literal the left operand is compared against. Its type
 * comes from the left operand, because that is where `GenerateBranchExpression`
 * reads the comparison's type from.
 */
export function buildFixedConditionOperand(value: unknown, dataType: BackEndValueType | undefined): ISymbol {
  const text: string = value === undefined || value === null ? "" : String(value);
  return {
    category: ESymbolCategory.fixed,
    label: "Fixed Value",
    description: text,
    isFixed: true,
    fixedValue: value,
    currentType: dataType ?? "Unknown",
    targetType: dataType ?? "Unknown"
  };
}

/**
 * A value the running process carries that is NOT one of its parameters — a
 * multi-instance counter, the initiator, the instance id. The symbol picker
 * offers these under "Common" as the same category with a FIXED keyword in
 * `inputName`, and the generator keys on that keyword, so it is the one field
 * that must be passed through verbatim.
 */
export function buildWorkflowInstanceOperand(inputName: string, label: string, dataType: string): ISymbol {
  const valueType: BackEndValueType = (dataType as BackEndValueType) ?? "Unknown";
  return {
    category: ESymbolCategory.processParameter,
    label,
    description: label,
    fieldName: label,
    inputName,
    currentType: valueType,
    targetType: valueType,
    fieldType: valueType
  };
}

/** One comparison row, for a left operand that is already resolved. */
export function buildWorkflowConditionRowWithOperand(
  row: IWorkflowConditionRowInput,
  leftOperand: ISymbol
): IConditionModel {
  return {
    id: newConditionId(),
    field1: leftOperand,
    conditionOperator: normalizeWorkflowConditionOperator(row.operator),
    field2: buildFixedConditionOperand(row.value, leftOperand.targetType)
  };
}

/** One comparison row against a workflow parameter. */
export function buildWorkflowConditionRow(
  row: IWorkflowConditionRowInput,
  parameter: IWFParameter | undefined
): IConditionModel {
  return buildWorkflowConditionRowWithOperand(row, buildWorkflowParameterOperand(row.propertyName, parameter));
}

/** The condition a gateway path / multi-instance step stores: a set of rows joined by AND or OR. */
export function buildWorkflowConditionGroup(rows: IConditionModel[], isOrGroup: boolean): IConditionModel {
  return {
    id: newConditionId(),
    isOrGroup,
    conditionSet: {
      type: isOrGroup ? ConditionSetType.or : ConditionSetType.and,
      conditions: rows
    }
  };
}
